import { useEffect, useState } from "react";
import { z } from "zod";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitEnquiry } from "@/lib/api/public.functions";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const schema = z.object({
  name: z.string().trim().min(1, "Please enter your preferred name").max(100),
  email: z.string().trim().email("Please enter a valid email").max(255),
  company: z.string().trim().min(1, "Please enter your company").max(200),
  phone: z.string().trim().min(1, "Please enter a phone number").max(30),
  designation: z.string().trim().max(120).optional().or(z.literal("")),
  message: z.string().trim().min(1, "Please add your question").max(2000),
});

type FormState = z.infer<typeof schema>;
type Errors = Partial<Record<keyof FormState, string>>;
type Step = "question" | "details" | "submitting" | "success";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultQuestion?: string;
};

const EMPTY: FormState = {
  name: "",
  email: "",
  company: "",
  phone: "",
  designation: "",
  message: "",
};

const COUNTRY_CODES: { code: string; label: string }[] = [
  { code: "+60", label: "🇲🇾 +60" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+62", label: "🇮🇩 +62" },
  { code: "+66", label: "🇹🇭 +66" },
  { code: "+84", label: "🇻🇳 +84" },
  { code: "+63", label: "🇵🇭 +63" },
  { code: "+86", label: "🇨🇳 +86" },
  { code: "+852", label: "🇭🇰 +852" },
  { code: "+886", label: "🇹🇼 +886" },
  { code: "+81", label: "🇯🇵 +81" },
  { code: "+82", label: "🇰🇷 +82" },
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+64", label: "🇳🇿 +64" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+1", label: "🇺🇸 +1" },
];

export function EnquiryDialog({ open, onOpenChange, defaultQuestion = "" }: Props) {
  const [form, setForm] = useState<FormState>({ ...EMPTY, message: defaultQuestion });
  const [errors, setErrors] = useState<Errors>({});
  const [step, setStep] = useState<Step>("question");
  const [phoneCode, setPhoneCode] = useState("+60");

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, message: defaultQuestion });
      setErrors({});
      setPhoneCode("+60");
      setStep(defaultQuestion.trim() ? "details" : "question");
    }
  }, [open, defaultQuestion]);

  function update<K extends keyof FormState>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function continueToDetails() {
    if (!form.message.trim()) {
      setErrors({ message: "Please add your question" });
      return;
    }
    setStep("details");
  }

  async function submitAll() {
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: Errors = {};
      for (const issue of parsed.error.issues) {
        const k = issue.path[0] as keyof FormState;
        if (!fieldErrors[k]) fieldErrors[k] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setStep("submitting");
    try {
      await submitEnquiry({
        data: {
          ...parsed.data,
          phone: `${phoneCode} ${parsed.data.phone}`.trim(),
          designation: parsed.data.designation || undefined,
          source: "enquiry_dialog",
        },
      });
      setStep("success");
    } catch (e) {
      setStep("details");
      toast.error(e instanceof Error ? e.message : "Something went wrong. Please try again.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-border p-0 sm:max-w-lg">
        {step === "success" ? (
          <div className="p-8 text-center">
            <div className="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-full bg-accent/15 text-gold">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h2 className="text-2xl font-semibold text-primary">Thanks — we've received your enquiry</h2>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              {form.name ? `${form.name.split(" ")[0]}, our team will be in touch as soon as possible.` : "Our team will be in touch as soon as possible."}
            </p>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="mt-7 inline-flex items-center justify-center border border-border bg-white px-5 py-2.5 text-base font-semibold text-primary hover:bg-primary/5"
            >
              Close
            </button>
          </div>
        ) : step === "question" ? (
          <div className="p-7">
            <DialogHeader className="text-left">
              <DialogTitle className="text-xl font-semibold text-primary">
                Ask anything about tax, audit, advisory or your business.
              </DialogTitle>
            </DialogHeader>

            <div className="mt-5">
              <textarea
                autoFocus
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    continueToDetails();
                  }
                }}
                rows={5}
                placeholder="Type your question…"
                className="w-full resize-none border border-border bg-white px-3.5 py-3 text-base text-primary focus:border-accent/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
              />
              {errors.message && <p className="mt-1 text-sm text-gold">{errors.message}</p>}
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">Press Enter to continue</p>
              <button
                type="button"
                onClick={continueToDetails}
                className="inline-flex items-center gap-2 border border-primary/30 bg-white px-5 py-2.5 text-base font-semibold text-primary transition hover:bg-primary/5"
              >
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitAll();
            }}
            className="p-7"
          >
            <DialogHeader className="text-left">
              <DialogTitle className="text-xl font-semibold text-primary">
                Share your details so we can get in touch
              </DialogTitle>
            </DialogHeader>

            {/* Question summary */}
            <div className="mt-5 border-l-2 border-accent/60 bg-muted/40 px-3 py-2.5">
              <div className="flex items-start justify-between gap-3">
                <p className="text-base leading-relaxed text-foreground/85 whitespace-pre-wrap">
                  {form.message}
                </p>
                <button
                  type="button"
                  onClick={() => setStep("question")}
                  className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold uppercase tracking-wider text-primary hover:underline"
                >
                  <ArrowLeft className="h-3 w-3" /> Edit
                </button>
              </div>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <Field label="Preferred Name" error={errors.name} required>
                <input
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="w-full border border-border bg-white px-3 py-2.5 text-base text-primary focus:border-accent/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                />
              </Field>
              <Field label="Email" error={errors.email} required>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="w-full border border-border bg-white px-3 py-2.5 text-base text-primary focus:border-accent/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                />
              </Field>
              <Field label="Company" error={errors.company} required>
                <input
                  value={form.company}
                  onChange={(e) => update("company", e.target.value)}
                  className="w-full border border-border bg-white px-3 py-2.5 text-base text-primary focus:border-accent/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                />
              </Field>
              <Field label="Phone" error={errors.phone} required>
                <div className="flex gap-2">
                  <select
                    aria-label="Country code"
                    value={phoneCode}
                    onChange={(e) => setPhoneCode(e.target.value)}
                    className="shrink-0 border border-border bg-white px-2 py-2.5 text-base text-primary focus:border-accent/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>{c.label}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    inputMode="tel"
                    placeholder="12 345 6789"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    className="w-full min-w-0 border border-border bg-white px-3 py-2.5 text-base text-primary focus:border-accent/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                  />
                </div>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Designation" error={errors.designation} optional>
                  <input
                    value={form.designation}
                    onChange={(e) => update("designation", e.target.value)}
                    className="w-full border border-border bg-white px-3 py-2.5 text-base text-primary focus:border-accent/60 focus:outline-none focus:ring-1 focus:ring-accent/30"
                  />
                </Field>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                By submitting, you agree to be contacted about your enquiry.
              </p>
              <button
                type="submit"
                disabled={step === "submitting"}
                className="inline-flex items-center gap-2 border border-primary/30 bg-white px-5 py-2.5 text-base font-semibold text-primary transition hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {step === "submitting" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Submit <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  error,
  required,
  optional,
  children,
}: {
  label: string;
  error?: string;
  required?: boolean;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-primary">
        {label}
        {required && <span className="text-gold"> *</span>}
        {optional && <span className="ml-1 font-normal normal-case tracking-normal text-muted-foreground">(optional)</span>}
      </span>
      {children}
      {error && <span className="mt-1 block text-sm text-gold">{error}</span>}
    </label>
  );
}
